import { allocate, money, splitEvenly, sum, type Money } from "./money.js";

/**
 * Come si divide una spesa.
 *
 * Vive in `@qota/shared` e non nel backend perché serve a entrambi i lati: il
 * server la usa come verità, il frontend per l'anteprima istantanea della
 * schermata 07 — «Equamente · 17,28 € a testa» deve aggiornarsi mentre si
 * digita, non dopo un giro di rete.
 *
 * Tutto qui dentro è puro: nessun I/O, nessuna dipendenza. Non è uno use case,
 * è dominio.
 */

export const SPLIT_MODES = ["equal", "shares", "percent", "exact"] as const;
export type SplitMode = (typeof SPLIT_MODES)[number];

export interface SplitParticipant {
  memberId: string;
  /**
   * Quote per `shares`, percentuale per `percent`. Ignorato per `equal`.
   * Per `exact` si usa `amountCents`.
   */
  weight?: number;
  /** Importo assegnato a mano. Solo per `exact`. */
  amountCents?: number;
}

export interface SplitShare {
  memberId: string;
  amount: Money;
  /** Il parametro originale, da rimostrare quando si riapre la divisione. */
  weight: number | null;
}

export class SplitError extends Error {
  constructor(
    readonly code:
      "no_participants" | "invalid_weight" | "weights_empty" | "percent_not_100" | "exact_mismatch",
    message: string,
    /** Quanto manca (positivo) o quanto avanza (negativo), quando ha senso. */
    readonly differenceCents?: number,
  ) {
    super(message);
    this.name = "SplitError";
  }
}

/** Tolleranza sulle percentuali: 100,001 è 100, l'utente non deve accorgersene. */
const PERCENT_EPSILON = 0.01;

function assertParticipants(participants: readonly SplitParticipant[]): void {
  if (participants.length === 0) {
    throw new SplitError("no_participants", "Serve almeno una persona fra cui dividere");
  }
}

function weightsOf(participants: readonly SplitParticipant[]): number[] {
  return participants.map((participant) => {
    const weight = participant.weight ?? 0;

    if (!Number.isFinite(weight) || weight < 0) {
      throw new SplitError("invalid_weight", "Le quote devono essere numeri non negativi");
    }

    return weight;
  });
}

/**
 * Calcola quanto tocca a ciascuno.
 *
 * GARANZIA: la somma delle quote restituite è **esattamente** uguale al totale,
 * in ogni modalità. È la proprietà che regge tutti i saldi dell'app.
 */
export function computeSplit(
  total: Money,
  mode: SplitMode,
  participants: readonly SplitParticipant[],
): SplitShare[] {
  assertParticipants(participants);

  switch (mode) {
    case "equal": {
      const amounts = splitEvenly(total, participants.length);

      return participants.map((participant, index) => ({
        memberId: participant.memberId,
        amount: amounts[index] ?? money(0, total.currency),
        weight: null,
      }));
    }

    case "shares": {
      const weights = weightsOf(participants);

      if (weights.every((weight) => weight === 0)) {
        throw new SplitError("weights_empty", "Assegna almeno una quota");
      }

      const amounts = allocate(total, weights);

      return participants.map((participant, index) => ({
        memberId: participant.memberId,
        amount: amounts[index] ?? money(0, total.currency),
        weight: weights[index] ?? 0,
      }));
    }

    case "percent": {
      const weights = weightsOf(participants);
      const totalPercent = weights.reduce((acc, weight) => acc + weight, 0);

      if (Math.abs(totalPercent - 100) > PERCENT_EPSILON) {
        throw new SplitError(
          "percent_not_100",
          `Le percentuali sommano a ${totalPercent.toFixed(2)}%, devono fare 100%`,
        );
      }

      const amounts = allocate(total, weights);

      return participants.map((participant, index) => ({
        memberId: participant.memberId,
        amount: amounts[index] ?? money(0, total.currency),
        weight: weights[index] ?? 0,
      }));
    }

    case "exact": {
      const amounts = participants.map((participant) => {
        const cents = participant.amountCents ?? 0;

        if (!Number.isInteger(cents)) {
          throw new SplitError("invalid_weight", "Gli importi esatti sono centesimi interi");
        }

        return cents;
      });

      const assigned = amounts.reduce((acc, cents) => acc + cents, 0);
      const difference = total.cents - assigned;

      if (difference !== 0) {
        throw new SplitError(
          "exact_mismatch",
          difference > 0
            ? `Mancano ancora ${(difference / 100).toFixed(2)} da assegnare`
            : `Hai assegnato ${(-difference / 100).toFixed(2)} di troppo`,
          difference,
        );
      }

      return participants.map((participant, index) => ({
        memberId: participant.memberId,
        amount: money(amounts[index] ?? 0, total.currency),
        weight: null,
      }));
    }
  }
}

/**
 * Quanto resta da assegnare, senza lanciare eccezioni.
 *
 * Alimenta il contatore in fondo alla schermata 07 mentre l'utente digita: lì
 * uno stato incompleto è normale, non un errore da mostrare in rosso.
 * Positivo = manca, negativo = eccede, zero = torna.
 */
export function splitRemainder(
  total: Money,
  mode: SplitMode,
  participants: readonly SplitParticipant[],
): number {
  if (mode !== "exact") {
    // Nelle altre modalità la ripartizione è proporzionale e chiude sempre.
    return 0;
  }

  const assigned = participants.reduce(
    (acc, participant) => acc + (participant.amountCents ?? 0),
    0,
  );

  return total.cents - assigned;
}

/** `true` se la divisione è valida così com'è. Non lancia. */
export function isSplitComplete(
  total: Money,
  mode: SplitMode,
  participants: readonly SplitParticipant[],
): boolean {
  try {
    computeSplit(total, mode, participants);
    return true;
  } catch {
    return false;
  }
}

/**
 * Verifica che i pagatori coprano esattamente il totale.
 *
 * Una spesa può essere stata anticipata da più persone («Ha pagato: Ugo e
 * Sara»), e la somma di quanto hanno messo deve fare il totale — altrimenti i
 * saldi non tornerebbero.
 */
export function validatePayers(
  total: Money,
  payers: readonly { memberId: string; amountCents: number }[],
): void {
  if (payers.length === 0) {
    throw new SplitError("no_participants", "Serve almeno un pagatore");
  }

  const paid = sum(
    payers.map((payer) => money(payer.amountCents, total.currency)),
    total.currency,
  );

  if (paid.cents !== total.cents) {
    const difference = total.cents - paid.cents;

    throw new SplitError(
      "exact_mismatch",
      difference > 0
        ? `I pagatori coprono ${(paid.cents / 100).toFixed(2)} su ${(total.cents / 100).toFixed(2)}`
        : `I pagatori coprono ${(paid.cents / 100).toFixed(2)}, più del totale`,
      difference,
    );
  }
}
